import { APP_POOL_CONFIG } from '@synergy/config';
import { ArrayHelpers, Field, FieldArray, FieldProps, FormikProps, useFormikContext } from 'formik';
import { Box, FormHelperText, TableCell, Theme } from '@mui/material';
import { FormComponent } from '@synergy/abstract';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Locale, UserRole } from '@synergy/models';
import {
    PALO_QUANTITY_HEAD_SX,
    PaloDirtyCell,
    PaloOverflowAction,
    PaloPositionColumn,
    PaloPositionTable,
    PaloQuantityCell,
    paloHasDescription,
} from '@synergy/components-palo';
import { PaloNextThemeProvider, tokens } from '@synergy/theme-palo';
import { PoolTextField, ProductDetails } from '@synergy/components';
import { ReactElement, cloneElement } from 'react';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { connect } from 'react-redux';
import { getCurrencySymbol, getLocalizedString, hasRole } from '@synergy/utils';
import { useEffect } from 'react';
import urljoin from 'url-join';

import { AppState } from '../../../../../store/models/app-state';
import { CustomPosition } from '../../../../../common/components/free-position-dialog/model/custom-position';
import { CustomPositionFields } from '../../../../../common/components/free-position-dialog/model/custom-position-fields';
import { FinancialCalculations } from '../../../../../common/utils/financial-calculations';
import { FreePositionDialog } from '../../../../../common/components/free-position-dialog/free-position-dialog';
import { GenericItemModal } from './generic-item-modal';
import { PaloPositionText } from '../../../../../common/components/palo/palo-position-text';
import { PaloPurchaseOrderTotalsBar } from '../../../palo/palo-purchase-order-totals-bar';
import { PaloRowProductSearch } from '../../../../../common/components/palo/palo-row-product-search';
import { PositionAttachmentsModal } from '../../../../../common/components/position-attachments/position-attachments-modal';
import { PositionEditModal } from './position-edit-modal';
import { Product } from '../../../../../common/models/product';
import { PurchaseOrderDetailPositionsFormValues } from '../purchase-order-detail-positions';
import { PurchaseOrderGenericItem, PurchaseOrderGenericItemType } from '../../../models/purchase-order-generic-item';
import {
    PurchaseOrderItem,
    isPurchaseOrderGenericItem,
    isPurchaseOrderProductItem,
} from '../../../models/purchase-order-item';
import { PurchaseOrderProductItem } from '../../../models/purchase-order-product-item';
import { ROUTES } from '../../../../sidebar-menu/navigation-drawer/routes';
import { Supplier } from '../../../../../common/models/supplier';
import { User } from '../../../../../common/models/user';
import { fieldNames, fieldNamesItems } from '../models/purchase-order-positions-form-values';
import { getNextPosition } from '../utils/get-next-position';
import { getProductPrice } from '../../../../../common/utils/get-product-price';
import { isBlankPosition } from '../../../../../common/components/palo/palo-first-position';
import { paloPositionMedia, setPaloPositionMedia } from '../../../../../common/components/palo/palo-position-media';
import { usePurchaseOrderDetailContext } from '../../context/purchase-order-context';

const styles = (theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            marginTop: '2.5rem',
        },
        content: {
            flex: 1,
        },
        container: {
            margin: '0 ' + theme.general.pageContentSpacing,
        },
        table: {
            padding: '10px 10px',
            marginBottom: 20,

            /* table head */
            '& thead tr': {
                height: 'auto',
            },
            '& thead tr th': {
                textAlign: 'left',
                margin: 0,
                whiteSpace: 'nowrap',
            },
            '& thead tr th:nth-child(1)': {
                textAlign: 'center',
            },
            /* A column of figures: heading and values on the same edge. The
               headings stood left while every number stood right. */
            '& thead tr th:nth-child(n + 3)': {
                textAlign: 'right',
            },

            /* table body */
            /* A row is as tall as its content once the fields stop adding
               their own height. */
            '& tbody tr td': {
                textAlign: 'right',
                paddingBottom: theme.spacing(1),
            },
            /* The number is a handle now, no longer a number between two
               arrows: it sits on the line of the row, not above it. */
            '& tbody tr td:nth-child(1)': {
                padding: 0,
                paddingLeft: 8,
                fontSize: 15,
                textAlign: 'center',
            },
            '& tbody tr td:nth-child(2)': {
                textAlign: 'left',
            },
            '& tbody tr td:last-child': {
                whiteSpace: 'nowrap',
            },

            /* No box around an untouched number. Ten rows of outlined fields
               read as a form, not as a table; ERPNext shows the plain value and
               draws a field only once the cursor is in one (#2208). The border
               comes back on hover, turns accent on focus and stays red on an
               error. */
            '& tbody .MuiOutlinedInput-root': {
                backgroundColor: 'transparent',
            },
            '& tbody .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
            },
            '& tbody .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: tokens.color.line,
            },
            '& tbody .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: tokens.color.accent,
                borderWidth: 1,
            },
            '& tbody .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                borderColor: tokens.color.crit,
            },
            '& tbody .MuiOutlinedInput-input': {
                paddingTop: 3,
                paddingBottom: 3,
            },
        },
        lastTableRow: {
            '& tr:last-child td': {
                paddingBottom: 12,
            },
        },
        product: {
            /* Nothing to line up with any more. */
            marginTop: 0,
        },
        inputQuantity: {
            width: '100%',
            '& > div > input': {
                textAlign: 'right',
            },
        },
        inputPrice: {
            width: 95,
            '& > div > input': {
                textAlign: 'right',
            },
        },
        inputDiscount: {
            width: 95,
            '& > div > input': {
                textAlign: 'right',
            },
        },
        inputTax: {
            width: 95,
            '& > div > input': {
                textAlign: 'right',
            },
        },
        positionValue: {
            paddingRight: 20,
        },
        customPositionIcon: {
            marginRight: theme.spacing(),
        },
        addPosition: {
            fontSize: '15px',
        },
    });

export type ItemsArrayHelper = ArrayHelpers & {
    form: FormikProps<PurchaseOrderDetailPositionsFormValues>;
    name: string;
};

interface DetailPositionsTableOwnProps {
    /** Sits under the table: adding a row, adding several. */
    toolbar?: ReactElement;
    purchaseOrderItems: PurchaseOrderItem[];
    supplier?: Supplier;
    currency: string;
    locale: Locale;
    appLocale: Locale;
    readonly?: boolean;
}

interface DetailPositionsTableContextProps {
    /** The supplier the head holds right now; see the wrapper below. */
    supplierId?: number;
    supplierName?: string;
    selectedProduct: Product | null;
    showAddPositionModal: boolean;
    setShowAddPositionModal?: (show: boolean) => void;
    showGenericItemModal: { show: boolean; type: PurchaseOrderGenericItemType };
    setShowGenericItemModal?: ({ show, type }: { show: boolean; type: PurchaseOrderGenericItemType }) => void;
}

interface DetailPositionsTableReduxProps extends WithStyles<typeof styles> {
    user: User;
    mediaBaseUrl: string;
}

type DetailPositionsTableProps = DetailPositionsTableOwnProps &
    DetailPositionsTableReduxProps &
    DetailPositionsTableContextProps;

interface CustomData {
    index?: number;
    discount?: number;
    wasNormArticle: boolean;
}

interface DetailPositionsTableState {
    /** The position whose attachments are open, if any. */
    attachmentsFor?: { index: number; position: PurchaseOrderProductItem };
    customPosition?: CustomPosition;
    showEditPositionModal: boolean;
    customData?: CustomData;
    genericItem?: PurchaseOrderGenericItem;
}
class DetailPositionsTableBase extends FormComponent<
    PurchaseOrderDetailPositionsFormValues,
    DetailPositionsTableProps,
    DetailPositionsTableState
> {
    public state: DetailPositionsTableState = {
        showEditPositionModal: false,
    };

    private lastQuantityField: HTMLInputElement | null = null;
    private setFocus = false;

    private editOf = (index: number, position: PurchaseOrderItem) =>
        isPurchaseOrderGenericItem(position)
            ? this.handleShowGenericModal(index, position)
            : position.product
            ? this.handleShowEditPositionModal(index, position)
            : this.handleShowAddFreePositionModal(index, position);

    /** Articles carry a description; surcharges keep theirs in the name. */
    private hasDescription = (position: PurchaseOrderItem): boolean =>
        isPurchaseOrderProductItem(position) &&
        paloHasDescription(
            value => getLocalizedString([this.props.locale], value) ?? '',
            position.shortDescription,
            position.longDescription
        );

    /** What one row offers; the table draws the menu around it. */
    private rowActions = (
        helper: ItemsArrayHelper,
        position: PurchaseOrderItem,
        index: number
    ): PaloOverflowAction[] => [
        // Drawings, photos and datasheets that belong to this
        // position (key-user brief A3.3).
        ...(isPurchaseOrderProductItem(position) && position.id
            ? [
                  {
                      label: (
                          <FormattedMessage
                              id="app.positions.attachments.tooltip"
                              values={{ count: paloPositionMedia(position.id, position.media).length }}
                          />
                      ),
                      onClick: () => this.setState({ attachmentsFor: { index, position } }),
                  },
              ]
            : []),
        ...(this.props.readonly
            ? []
            : [
                  {
                      label: <FormattedMessage id="app.common.edit" />,
                      onClick: this.editOf(index, position),
                  },
                  {
                      label: <FormattedMessage id="app.positions.insertAbove" />,
                      onClick: () => this.insertPosition(helper, index, 'above'),
                  },
                  {
                      label: <FormattedMessage id="app.positions.insertBelow" />,
                      onClick: () => this.insertPosition(helper, index, 'below'),
                  },
                  {
                      label: <FormattedMessage id="app.common.duplicate" />,
                      onClick: () => this.duplicatePosition(helper, index),
                  },
                  {
                      label: <FormattedMessage id="app.common.delete" />,
                      danger: true,
                      onClick: () => this.removePosition(helper, index),
                  },
              ]),
    ];

    /**
     * The handles of a row, for the dialog that edits it.
     *
     * The row menu and the dialog offer the same things, so one is not a
     * shorter version of the other; acting closes the dialog, because what it
     * was showing has moved or is gone (#2208).
     */
    private rowHandles = (helper: ItemsArrayHelper, index?: number) => {
        if (undefined === index) {
            return {};
        }

        const andClose = (act: () => void) => () => {
            act();
            this.handleCloseEditPositionModal();
        };

        return {
            onInsertAbove: andClose(() => this.insertPosition(helper, index, 'above')),
            onInsertBelow: andClose(() => this.insertPosition(helper, index, 'below')),
            onDuplicate: andClose(() => this.duplicatePosition(helper, index)),
            onDelete: andClose(() => this.removePosition(helper, index)),
        };
    };

    /** The columns the order has of its own; the rest belongs to the table. */
    private columns = (): PaloPositionColumn<PurchaseOrderItem>[] => [
        { key: 'name', type: 'name', label: <FormattedMessage id={'app.table.column.article'} /> },
        {
            key: 'quantity',
            type: 'quantity',
            label: <FormattedMessage id={'app.table.column.quantity'} />,
            headSx: PALO_QUANTITY_HEAD_SX,
        },
        {
            key: 'price',
            type: 'price',
            label: (
                <FormattedMessage
                    id={'app.table.column.priceWithValue'}
                    values={{ value: getCurrencySymbol(this.props.currency, this.props.locale) }}
                />
            ),
        },
        {
            key: 'discount',
            type: 'percent',
            label: <FormattedMessage id={'app.table.column.discountWithPercentage'} />,
        },
        { key: 'tax', type: 'percent', label: <FormattedMessage id={'app.table.column.taxWithPercentage'} /> },
        {
            key: 'positionValue',
            type: 'amount',
            label: (
                <FormattedMessage
                    id={'app.table.column.positionValue'}
                    values={{ value: getCurrencySymbol(this.props.currency, this.props.locale) }}
                />
            ),
        },
    ];

    /**
     * Swaps a position with its neighbour. It moves both the stored `position`
     * and the place in the array, because the list renders in array order while
     * the backend sorts by the field — leaving one behind would put the rows
     * back on the next load.
     */
    private dragFromRef: number | null = null;

    /**
     * Carries a position to another place, in one write.
     *
     * The order and the numbering have to land together: renumbering after
     * Formik's own move reads the list as it was before, and the sort puts
     * every row back where it came from (#2208).
     */
    private writeItems = (helper: ItemsArrayHelper, items: PurchaseOrderItem[]) => {
        items.forEach((item, index) => {
            item.position = index + 1;
        });

        helper.form.setFieldValue(fieldNames.allItems, items);
    };

    private movePosition = (helper: ItemsArrayHelper, from: number, to: number) => {
        const items = [...(helper.form.values.allItems as PurchaseOrderItem[])];
        const [moved] = items.splice(from, 1);
        items.splice(to, 0, moved);
        this.writeItems(helper, items);
    };

    private insertPosition = (helper: ItemsArrayHelper, index: number, where: 'above' | 'below') => {
        this.addBlankRow(helper, 'above' === where ? index : index + 1);
    };

    /**
     * A row that arrives empty and waits for its article.
     *
     * It is ERPNext's "Add row": the row is there first, and what it is about
     * is searched for in the row itself. The free position keeps its own
     * dialog, because it has no article to find (#2208).
     */
    private addBlankRow = (helper: ItemsArrayHelper, at?: number) => {
        const items = [...(helper.form.values.allItems as PurchaseOrderItem[])];
        items.splice(
            undefined === at ? items.length : at,
            0,
            PurchaseOrderProductItem.fromPlainObject({
                name: {},
                quantity: 1,
                price: 0,
                discount: 0,
                tax: 0,
                orderUnit: '',
                isBestPrice: false,
                currency: this.props.currency,
            })
        );
        this.writeItems(helper, items);
    };

    /** Fills a blank row with the article searched for in it, in its place. */
    private pickProduct = (helper: ItemsArrayHelper, index: number, product: Product) => {
        const items = [...(helper.form.values.allItems as PurchaseOrderItem[])];
        const snapshot = PurchaseOrderProductItem.fromProduct(
            product,
            this.props.locale,
            this.props.currency,
            APP_POOL_CONFIG.address.country
        );
        items[index] = PurchaseOrderProductItem.fromPlainObject({
            ...snapshot,
            position: items[index].position,
        });
        this.writeItems(helper, items);
    };

    /** Takes a position out and closes the numbering behind it. */
    private removePosition = (helper: ItemsArrayHelper, index: number) => {
        const items = [...(helper.form.values.allItems as PurchaseOrderItem[])];
        items.splice(index, 1);
        this.writeItems(helper, items);
    };

    /** A position again, without the id that ties it to a saved one. */
    private copyOf = (source: PurchaseOrderItem): PurchaseOrderItem =>
        (isPurchaseOrderGenericItem(source)
            ? { ...source, id: undefined }
            : PurchaseOrderProductItem.fromPlainObject({ ...source, id: undefined })) as PurchaseOrderItem;

    private duplicatePosition = (helper: ItemsArrayHelper, index: number) => {
        const items = [...(helper.form.values.allItems as PurchaseOrderItem[])];

        items.splice(index + 1, 0, this.copyOf(items[index]));
        this.writeItems(helper, items);
    };

    private getRow = (helper: ItemsArrayHelper) => (position: PurchaseOrderItem, index: number) => {
        const prefix = `${fieldNames.allItems}.${index}`;
        const url =
            (isPurchaseOrderProductItem(position) &&
                position.product &&
                urljoin(ROUTES.products.product.base, position.product.id.toString())) ||
            undefined;

        const handleQuantityChange = (
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            form: FormikProps<any>,
            field: FieldProps<string>['field']
        ): void => {
            const quantity = e.target.value;
            const price = isPurchaseOrderProductItem(position)
                ? getProductPrice(
                      form.getFieldProps(`${prefix}.${fieldNamesItems.price}`).value,
                      this.props.currency,
                      Number(quantity),
                      position.product
                  )
                : FinancialCalculations.getNetTotal(position);
            form.setFieldValue(field.name, quantity);
            form.setFieldValue(`${prefix}.${fieldNamesItems.price}`, price);
        };

        // A row that carries neither an article nor a name of its own is one
        // that was just added and is waiting for one.
        const isBlank =
            isPurchaseOrderProductItem(position) &&
            !position.product &&
            0 === Object.values(position.name ?? {}).filter(Boolean).length;

        const displayWrapper = isPurchaseOrderProductItem(position)
            ? position
            : {
                  ...position,
                  name: { [this.props.locale]: position.title },
                  getDescription: () => ({ [this.props.locale]: position.description }),
              };

        return {
            id: index,
            name: isBlank ? (
                /* A row added with "Zeile hinzufügen" starts empty, and the
                   article is searched for right here — the way ERPNext fills a
                   new row in the row itself (#2208). */
                <PaloRowProductSearch
                    onSelected={product => this.pickProduct(helper, index, product)}
                    supplierIds={this.props.supplierId ? [this.props.supplierId] : undefined}
                    supplierName={this.props.supplierName}
                    supplierMissing={!this.props.supplierId}
                    includeNormArticles={true}
                />
            ) : (
                <>
                    <ProductDetails
                        product={displayWrapper}
                        showDescription={!isPurchaseOrderProductItem(position)}
                        inlineNumber={true}
                        maxLines={2}
                        url={
                            hasRole([UserRole.Admin, UserRole.SuperAdmin, UserRole.Sales], this.props.user)
                                ? url
                                : undefined
                        }
                        className={this.props.classes.product}
                    />
                    {isPurchaseOrderProductItem(position) && (
                        <PaloPositionText
                            index={index}
                            shortDescription={position.shortDescription}
                            longDescription={position.longDescription}
                            onEdit={this.props.readonly ? undefined : this.editOf(index, position)}
                        />
                    )}
                </>
            ),
            quantity: (
                /* The unit belongs to the figure, not above it as a caption:
                   one position in nine is not "Stück". */
                <PaloQuantityCell unit={isPurchaseOrderProductItem(position) ? position.orderUnit : undefined}>
                    <PaloDirtyCell name={`${prefix}.${fieldNamesItems.quantity}`}>
                        <Field name={`${prefix}.${fieldNamesItems.quantity}`}>
                            {({ field, form }: FieldProps<string>) => (
                                <PoolTextField
                                    inputRef={ref => (this.lastQuantityField = ref)}
                                    {...field}
                                    fullWidth={true}
                                    fieldType="dense"
                                    // No label: the column above the field already says
                                    // "Menge", and the two read as the word twice.
                                    error={this.hasError(form, field.name)}
                                    helperText={this.getError(form, field.name)}
                                    readOnly={this.props.readonly}
                                    className={this.props.classes.inputQuantity}
                                    isNumeric={true}
                                    onChange={e => handleQuantityChange(e, form, field)}
                                />
                            )}
                        </Field>
                    </PaloDirtyCell>
                </PaloQuantityCell>
            ),
            price: (
                <PaloDirtyCell name={`${prefix}.${fieldNamesItems.price}`}>
                    <Field name={`${prefix}.${fieldNamesItems.price}`}>
                        {({ field, form }: FieldProps<number>) => (
                            <PoolTextField
                                {...field}
                                value={field.value}
                                id={field.name}
                                fieldType="dense"
                                className={this.props.classes.inputPrice}
                                error={this.hasError(form, field.name)}
                                helperText={this.getError(form, field.name)}
                                readOnly={this.props.readonly}
                                isNumeric={true}
                            />
                        )}
                    </Field>
                </PaloDirtyCell>
            ),
            discount: (
                <PaloDirtyCell name={`${prefix}.${fieldNamesItems.discount}`}>
                    <Field name={`${prefix}.${fieldNamesItems.discount}`}>
                        {({ field, form }: FieldProps<string>) => (
                            <PoolTextField
                                {...field}
                                fieldType="dense"
                                className={this.props.classes.inputDiscount}
                                error={this.hasError(form, field.name)}
                                helperText={this.getError(form, field.name)}
                                readOnly={this.props.readonly}
                                isNumeric={true}
                                minNumber={0}
                                maxNumber={100}
                            />
                        )}
                    </Field>
                </PaloDirtyCell>
            ),
            // A tax-free order charges no VAT, whatever rate the article carries (#2263).
            tax: helper.form.values.isTaxFree ? (
                <span>–</span>
            ) : isPurchaseOrderGenericItem(position) ? (
                <PaloDirtyCell name={`${prefix}.${fieldNamesItems.tax}`}>
                    <Field name={`${prefix}.${fieldNamesItems.tax}`}>
                        {({ field, form }: FieldProps<string>) => (
                            <PoolTextField
                                {...field}
                                fieldType="dense"
                                className={this.props.classes.inputTax}
                                error={this.hasError(form, field.name)}
                                helperText={this.getError(form, field.name)}
                                readOnly={this.props.readonly}
                                isNumeric={true}
                                minNumber={0}
                                maxNumber={100}
                            />
                        )}
                    </Field>
                </PaloDirtyCell>
            ) : (
                <span>{position.tax || 0}%</span>
            ),
            positionValue: (
                <span className={this.props.classes.positionValue}>
                    <FormattedNumber
                        currency={this.props.currency}
                        value={FinancialCalculations.getNetTotal(position)}
                        style="currency"
                    />
                </span>
            ),
        };
    };

    private handleShowAddFreePositionModal = (index?: number, customPosition?: PurchaseOrderProductItem) => () => {
        this.setState({
            customPosition,
            customData:
                customPosition && index !== undefined
                    ? {
                          index,
                          discount: customPosition.discount,
                          wasNormArticle: customPosition.isNormArticle || false,
                      }
                    : undefined,
        });
        this.props.setShowAddPositionModal?.(true);
    };

    private handleShowGenericModal = (index: number, item: PurchaseOrderGenericItem) => () => {
        this.setState({
            genericItem: item,
            customData: {
                index,
                wasNormArticle: false,
            },
        });
        this.props.setShowGenericItemModal?.({ show: true, type: item.type });
    };

    private handleShowEditPositionModal = (index?: number, position?: PurchaseOrderProductItem) => () => {
        this.setState({
            showEditPositionModal: true,
            customData:
                position && index !== undefined
                    ? {
                          index,
                          wasNormArticle: position.isNormArticle || false,
                      }
                    : undefined,
        });
    };

    private handleCloseAddFreePositionModal = () => {
        this.setState({ customPosition: undefined, customData: undefined });
        this.props.setShowAddPositionModal?.(false);
    };

    private handleCloseEditPositionModal = () => {
        this.setState({ showEditPositionModal: false, customData: undefined });
    };

    private handleCloseGenericItemModal = () => {
        this.setState({ genericItem: undefined, customData: undefined });
        this.props.setShowGenericItemModal?.({ show: false, type: PurchaseOrderGenericItemType.Surcharge });
    };

    private handleCustomPositionSave = (helper: ItemsArrayHelper) => (value: CustomPosition, data?: unknown) => {
        const customData = data as CustomData;

        const item: PurchaseOrderProductItem = PurchaseOrderProductItem.fromPlainObject({
            ...value,
            currency: this.props.currency,
            isBestPrice: false,
            discount: customData?.discount || 0,
        });

        if (customData?.index !== undefined) {
            if (customData.wasNormArticle !== value.isNormArticle) {
                item.id = undefined;
            }
            helper.replace(customData.index, item);
        } else {
            item.position = getNextPosition(helper.form);
            helper.push(item);
        }
        this.handleCloseAddFreePositionModal();
    };

    private handleGenericItemsSave = (helper: ItemsArrayHelper) => (item: PurchaseOrderGenericItem) => {
        if (this.state.customData?.index !== undefined) {
            helper.replace(this.state.customData.index, item);
        } else {
            item.position = getNextPosition(helper.form);
            helper.push(item);
        }
        this.handleCloseGenericItemModal();
    };

    public componentDidUpdate() {
        if (this.setFocus && this.lastQuantityField) {
            this.setFocus = false;
            this.lastQuantityField.focus();
        }
    }

    public render() {
        const { classes, purchaseOrderItems, readonly } = this.props;

        return (
            <FieldArray name={fieldNames.allItems}>
                {helper => (
                    <div>
                        {/* The switch that filters by supplier stands beside the
                            heading of the section now. */}
                        <PaloPositionTable
                            toolbar={
                                this.props.toolbar &&
                                cloneElement(this.props.toolbar as ReactElement<{ onAddRow?: () => void }>, {
                                    onAddRow: () => this.addBlankRow(helper),
                                })
                            }
                            items={purchaseOrderItems}
                            currency={this.props.currency}
                            quantityOf={item => item.quantity}
                            amountOf={item => FinancialCalculations.getNetTotal(item)}
                            columns={this.columns()}
                            getRowKey={(item, index) => `${item.id ?? 'new'}-${index}`}
                            hasDescription={this.hasDescription}
                            // The blank first row, when the order cannot go
                            // on without a complete position (#2308).
                            isRowFaulty={item =>
                                this.hasError(helper.form, fieldNames.allItems) &&
                                'string' === typeof helper.form.errors[fieldNames.allItems] &&
                                isPurchaseOrderProductItem(item) &&
                                isBlankPosition(item)
                            }
                            readonly={!!this.props.readonly}
                            onMove={(from, to) => this.movePosition(helper, from, to)}
                            rowActions={(item, index) => this.rowActions(helper, item, index)}
                            onDeleteMany={indices => {
                                const items = [...(helper.form.values.allItems as PurchaseOrderItem[])];
                                [...indices].sort((left, right) => right - left).forEach(i => items.splice(i, 1));
                                this.writeItems(helper, items);
                            }}
                            onDuplicateMany={indices => {
                                // One array, one write: duplicating per index
                                // reads the list as it was before the previous
                                // copy, so only the last one survived.
                                const items = [...(helper.form.values.allItems as PurchaseOrderItem[])];

                                [...indices]
                                    .sort((left, right) => right - left)
                                    .forEach(index => items.splice(index + 1, 0, this.copyOf(items[index])));
                                this.writeItems(helper, items);
                            }}
                            renderRow={(item, index) => {
                                const cells = this.getRow(helper)(item, index);

                                return (
                                    <>
                                        <TableCell>{cells.name}</TableCell>
                                        <TableCell>{cells.quantity}</TableCell>
                                        <TableCell>{cells.price}</TableCell>
                                        <TableCell>{cells.discount}</TableCell>
                                        <TableCell>{cells.tax}</TableCell>
                                        <TableCell>{cells.positionValue}</TableCell>
                                    </>
                                );
                            }}
                        />
                        <Field name={fieldNames.allItems}>
                            {({ field, form }: FieldProps<PurchaseOrderItem[]>) =>
                                this.hasError(form, field.name) && (
                                    <FormHelperText error={true}>{this.getError(form, field.name)}</FormHelperText>
                                )
                            }
                        </Field>

                        <PaloPurchaseOrderTotalsBar currency={this.props.currency} readonly={readonly} />

                        <PaloNextThemeProvider>
                            <FreePositionDialog
                                customPosition={this.state.customPosition}
                                customData={this.state.customData}
                                isOpen={this.props.showAddPositionModal}
                                currencySymbol={this.props.currency}
                                locale={this.props.appLocale}
                                requiredFields={[
                                    CustomPositionFields.Quantity,
                                    CustomPositionFields.OrderUnit,
                                    CustomPositionFields.Price,
                                    CustomPositionFields.Tax,
                                ]}
                                onCancel={this.handleCloseAddFreePositionModal}
                                onSave={this.handleCustomPositionSave(helper)}
                            />

                            {this.state.attachmentsFor?.position.id && (
                                <PositionAttachmentsModal
                                    kind="purchase-order-items"
                                    positionId={this.state.attachmentsFor.position.id}
                                    positionName={getLocalizedString(
                                        [this.props.locale],
                                        this.state.attachmentsFor.position.name
                                    )}
                                    media={paloPositionMedia(
                                        this.state.attachmentsFor.position.id,
                                        this.state.attachmentsFor.position.media
                                    )}
                                    mediaBaseUrl={this.props.mediaBaseUrl}
                                    readonly={readonly}
                                    onClose={() => this.setState({ attachmentsFor: undefined })}
                                    // Saved by the dialog itself: no input of the form (R-198).
                                    onChange={media =>
                                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                        setPaloPositionMedia(this.state.attachmentsFor!.position.id!, media)
                                    }
                                />
                            )}

                            <GenericItemModal
                                genericItem={this.state.genericItem}
                                showGenericItemModal={this.props.showGenericItemModal.show}
                                genericItemType={this.props.showGenericItemModal.type}
                                onModalClose={this.handleCloseGenericItemModal}
                                onSave={this.handleGenericItemsSave(helper)}
                            />

                            <PositionEditModal
                                showPositionEditModal={this.state.showEditPositionModal}
                                positionIndex={this.state.customData?.index}
                                readonly={readonly}
                                currency={this.props.currency}
                                onModalClose={this.handleCloseEditPositionModal}
                                {...this.rowHandles(helper, this.state.customData?.index)}
                            />
                        </PaloNextThemeProvider>
                    </div>
                )}
            </FieldArray>
        );
    }
}

const mapStateToProps = (state: AppState) => ({
    user: state.user,
    mediaBaseUrl: state.settings.mediaBaseUrl,
});

const ConnectedDetailPositionsTable = connect(mapStateToProps, {})(DetailPositionsTableBase);

const DetailPositionsTableWithStyles = withStyles(styles)(ConnectedDetailPositionsTable);

export const DetailPositionsTable = (props: DetailPositionsTableOwnProps) => {
    const {
        supplierAccountId,
        supplierAccountName,
        selectedProduct,
        setSelectedProduct,
        showAddPositionModal,
        setShowAddPositionModal,
        showGenericItemModal,
        setShowGenericItemModal,
    } = usePurchaseOrderDetailContext();
    const formik = useFormikContext<PurchaseOrderDetailPositionsFormValues>();

    useEffect(() => {
        if (selectedProduct) {
            const purchaseOrderProduct = PurchaseOrderProductItem.fromProduct(
                selectedProduct,
                props.locale,
                props.currency,
                APP_POOL_CONFIG.address.country
            );
            const purchaseOrderItem = PurchaseOrderProductItem.fromPlainObject(purchaseOrderProduct);
            purchaseOrderItem.position = getNextPosition(formik);

            formik.setFieldValue('allItems', [...formik.values.allItems, purchaseOrderItem]);
            setSelectedProduct?.(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProduct]);

    return (
        <DetailPositionsTableWithStyles
            {...props}
            supplierId={undefined !== supplierAccountId ? supplierAccountId ?? undefined : props.supplier?.id}
            supplierName={undefined !== supplierAccountId ? supplierAccountName : props.supplier?.name}
            selectedProduct={selectedProduct}
            showAddPositionModal={showAddPositionModal}
            setShowAddPositionModal={setShowAddPositionModal}
            showGenericItemModal={showGenericItemModal}
            setShowGenericItemModal={setShowGenericItemModal}
        />
    );
};
